/**
 * What a job's tally counted, in Japanese — looked up from the English key the server stored.
 *
 * ## Why this is on the client when the job's own name is not
 *
 * Job names and descriptions are translated in `server/…/jobs/registry.py`, because they are
 * constants: the registry is the only thing that has ever produced them, so changing the word
 * changes every rendering of it at once.
 *
 * **A tally key is not a constant. It is stored data.** `job_run.tally` is a JSONB column and every
 * run ever recorded holds its keys as written — `{"Audio added": 18}`. Translating the key at the
 * source would leave the whole run history speaking English while new runs spoke Japanese, in one
 * panel, with no migration short of rewriting the rows. So the stored key stays the stable handle
 * and this is the display layer over it — which also means a run from last year draws in Japanese.
 *
 * ## It fails open
 *
 * An unknown key renders as itself. A job added later reports something readable rather than a
 * blank tile, and a key renamed on the server degrades to English instead of disappearing — which
 * is the failure you want from a lookup that only exists to make numbers legible.
 *
 * ## The unit is not part of the key
 *
 * `schedule.ts` strips a trailing `(MB)` off the key before asking here, and appends it to the
 * *value* instead — so this table holds `Saved`, not `Saved (MB)`. That is the existing contract
 * and it is why translating these did not need the unit convention to change.
 */
const TALLY_LABELS: Record<string, string> = {
  // frequency-sort
  "Cards repositioned": "並べ替え",
  // audio — the canvas draws this row: `1,204件確認 · エラー17 · 音源なし12`
  "Audio added": "音声を追加",
  "No source found": "音源なし",
  "Bad reading": "読みが不正",
  Errors: "エラー",
  // prio-cleanup — the canvas draws `64件除去 · エラー0`
  "Tag cleared": "タグを除去",
  "Still new": "新規のまま",
  // heal-leeches
  "Notes tagged": "タグ付き",
  Healed: "解除",
  "Still failing": "失敗が続く",
  "Suspended skipped": "停止中を除外",
  // stats-calibration
  "Reviews scored": "採点した復習",
  "Cards scored": "採点したカード",
  "Cards skipped": "除外したカード",
  "Bands measured": "測定した帯",
  // notetype-fields
  "Fields added": "追加した項目",
  "Fields checked": "確認した項目",
  // fix-formatting
  "Notes cleaned": "整形したノート",
  "Fields rewritten": "書き換えた項目",
  "Line breaks kept": "残した改行",
  "Tags unwrapped": "外したタグ",
  "Entities decoded": "戻した実体参照",
  "Fields emptied": "空にした項目",
  "Needs review": "要確認",
  // fill-sentence-target — `Needs review` above is shared with fix-formatting, deliberately: it
  // means the same thing on both, and one key with one word is what keeps them saying it the same.
  "Targets recorded": "記録した対象語",
  "No word found": "該当なし",
  "Already recorded": "記録済み",
  "No sentence": "例文なし",
  // shrink-media
  "Images referenced": "参照された画像",
  "Re-encoded": "再エンコード",
  "Notes updated": "更新したノート",
  "Fields updated": "更新した項目",
  "Left alone": "変更なし",
  Saved: "削減",
  // prune-media
  "Files in store": "保管庫のファイル",
  Unused: "未参照",
  Trashed: "ゴミ箱へ",
  Missing: "見当たらない",
  "Staged captures swept": "一時キャプチャを削除",
  "Staged captures kept": "一時キャプチャを保持",
  "Upload slots swept": "アップロード枠を削除",
  "Trash emptied": "ゴミ箱を空に",
  "Trash freed": "ゴミ箱から回収",
  // backup-snapshot
  "Notes captured": "取得したノート",
  "Snapshot size": "スナップショット",
  "Snapshots kept": "保持世代",
  "Snapshots pruned": "削除世代",
};

export function tallyLabel(key: string): string {
  return TALLY_LABELS[key] ?? key;
}
